import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from './Button';
import { IconButton } from './IconButton';

type TranslationNamespace = string | readonly string[];

export type ToastTone = 'success' | 'error' | 'warning' | 'info';
export type ToastPlacement =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right';

export interface ToastProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title' | 'children'> {
  title?: string;
  titleKey?: string;
  message?: React.ReactNode;
  messageKey?: string;
  tone?: ToastTone;
  icon?: React.ReactNode;
  actionLabel?: string;
  actionLabelKey?: string;
  onAction?: () => void;
  closeOnAction?: boolean;
  onDismiss?: () => void;
  dismissible?: boolean;
  dismissLabel?: string;
  dismissLabelKey?: string;
  translationNs?: TranslationNamespace;
  data?: unknown;
}

export interface ToastInput extends Omit<ToastProps, 'onDismiss'> {
  id?: string;
  duration?: number;
}

interface StoredToast extends ToastInput {
  id: string;
  duration: number;
}

interface ToastContextValue {
  showToast: (toast: ToastInput) => string;
  dismissToast: (id: string) => void;
  dismissAll: () => void;
  success: (message: React.ReactNode, options?: Omit<ToastInput, 'message' | 'tone'>) => string;
  error: (message: React.ReactNode, options?: Omit<ToastInput, 'message' | 'tone'>) => string;
  warning: (message: React.ReactNode, options?: Omit<ToastInput, 'message' | 'tone'>) => string;
  info: (message: React.ReactNode, options?: Omit<ToastInput, 'message' | 'tone'>) => string;
}

export interface ToastApi {
  show: (toast: ToastInput) => string;
  success: (message: React.ReactNode, options?: Omit<ToastInput, 'message' | 'tone'>) => string;
  error: (message: React.ReactNode, options?: Omit<ToastInput, 'message' | 'tone'>) => string;
  warning: (message: React.ReactNode, options?: Omit<ToastInput, 'message' | 'tone'>) => string;
  warn: (message: React.ReactNode, options?: Omit<ToastInput, 'message' | 'tone'>) => string;
  info: (message: React.ReactNode, options?: Omit<ToastInput, 'message' | 'tone'>) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

export interface ToastProviderProps {
  children: React.ReactNode;
  placement?: ToastPlacement;
  maxVisible?: number;
  defaultDuration?: number;
  portalContainer?: HTMLElement | null;
  translationNs?: TranslationNamespace;
}

const toastToneClasses: Record<ToastTone, { border: string; bg: string; icon: string; title: string; message: string }> = {
  success: {
    border: 'border-emerald-500 [.theme-liquid-glass_&]:border-emerald-400/40',
    bg: 'bg-emerald-50 [.theme-liquid-glass_&]:bg-emerald-500/10',
    icon: 'text-emerald-500 [.theme-liquid-glass_&]:text-emerald-300',
    title: 'text-emerald-900 [.theme-liquid-glass_&]:text-emerald-100',
    message: 'text-emerald-800 [.theme-liquid-glass_&]:text-emerald-100/80',
  },
  error: {
    border: 'border-rose-500 [.theme-liquid-glass_&]:border-rose-400/40',
    bg: 'bg-rose-50 [.theme-liquid-glass_&]:bg-rose-500/10',
    icon: 'text-rose-500 [.theme-liquid-glass_&]:text-rose-300',
    title: 'text-rose-900 [.theme-liquid-glass_&]:text-rose-100',
    message: 'text-rose-800 [.theme-liquid-glass_&]:text-rose-100/80',
  },
  warning: {
    border: 'border-amber-500 [.theme-liquid-glass_&]:border-amber-400/40',
    bg: 'bg-amber-50 [.theme-liquid-glass_&]:bg-amber-500/10',
    icon: 'text-amber-500 [.theme-liquid-glass_&]:text-amber-300',
    title: 'text-amber-900 [.theme-liquid-glass_&]:text-amber-100',
    message: 'text-amber-800 [.theme-liquid-glass_&]:text-amber-100/80',
  },
  info: {
    border: 'border-sky-500 [.theme-liquid-glass_&]:border-sky-400/40',
    bg: 'bg-sky-50 [.theme-liquid-glass_&]:bg-sky-500/10',
    icon: 'text-sky-500 [.theme-liquid-glass_&]:text-sky-300',
    title: 'text-sky-900 [.theme-liquid-glass_&]:text-sky-100',
    message: 'text-sky-800 [.theme-liquid-glass_&]:text-sky-100/80',
  },
};

const placementClasses: Record<ToastPlacement, string> = {
  'top-left': 'top-4 left-4 items-start',
  'top-center': 'top-4 left-1/2 -translate-x-1/2 items-center',
  'top-right': 'top-4 right-4 items-end',
  'bottom-left': 'bottom-4 left-4 items-start',
  'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2 items-center',
  'bottom-right': 'bottom-4 right-4 items-end',
};

const iconByTone: Record<ToastTone, React.ReactNode> = {
  success: <CheckCircle2 size={18} aria-hidden="true" />,
  error: <XCircle size={18} aria-hidden="true" />,
  warning: <AlertTriangle size={18} aria-hidden="true" />,
  info: <Info size={18} aria-hidden="true" />,
};

const ToastContext = React.createContext<ToastContextValue | null>(null);
let globalToastApi: ToastContextValue | null = null;
let pendingToasts: ToastInput[] = [];
let pendingIdCounter = 0;

const getAnimationOffset = (placement: ToastPlacement) =>
  placement.startsWith('top') ? -16 : 16;

export const Toast: React.FC<ToastProps> = ({
  title,
  titleKey,
  message,
  messageKey,
  tone = 'info',
  icon,
  actionLabel,
  actionLabelKey,
  onAction,
  closeOnAction = true,
  onDismiss,
  dismissible = true,
  dismissLabel,
  dismissLabelKey,
  translationNs,
  className,
  ...props
}) => {
  const { data, ...htmlProps } = props;
  void data;
  const { t } = useTranslation(translationNs);
  const toneClasses = toastToneClasses[tone];
  const resolvedTitle = titleKey ? t(titleKey) : title;
  const resolvedMessage = messageKey ? t(messageKey) : message;
  const resolvedActionLabel = actionLabelKey ? t(actionLabelKey) : actionLabel;
  const resolvedDismissLabel = dismissLabelKey ? t(dismissLabelKey) : dismissLabel || t('common.close', 'Close');
  const ariaRole = tone === 'error' || tone === 'warning' ? 'alert' : 'status';
  const renderedIcon = icon ?? iconByTone[tone];

  const handleActionClick = () => {
    onAction?.();
    if (closeOnAction) {
      onDismiss?.();
    }
  };

  return (
    <div
      role={ariaRole}
      aria-live={ariaRole === 'alert' ? 'assertive' : 'polite'}
      className={clsx(
        'w-full max-w-sm rounded-2xl border-2 border-black bg-white shadow-[6px_6px_0px_rgba(0,0,0,1)]',
        '[.theme-liquid-glass_&]:rounded-3xl [.theme-liquid-glass_&]:border [.theme-liquid-glass_&]:border-white/20',
        '[.theme-liquid-glass_&]:bg-slate-900/85 [.theme-liquid-glass_&]:backdrop-blur-xl [.theme-liquid-glass_&]:shadow-[0_20px_48px_rgba(0,0,0,0.4)]',
        toneClasses.border,
        toneClasses.bg,
        className
      )}
      {...htmlProps}
    >
      <div className="flex items-start gap-3 p-4">
        <div className={clsx('mt-0.5 shrink-0', toneClasses.icon)}>{renderedIcon}</div>

        <div className="min-w-0 flex-grow">
          {resolvedTitle && (
            <div className={clsx('text-sm font-extrabold leading-tight', toneClasses.title)}>
              {resolvedTitle}
            </div>
          )}
          {resolvedMessage && (
            <div className={clsx('mt-1 text-sm leading-relaxed', toneClasses.message)}>
              {resolvedMessage}
            </div>
          )}

          {resolvedActionLabel && onAction && (
            <div className="mt-3">
              <Button
                text={resolvedActionLabel}
                variant="ghost"
                size="sm"
                onClick={handleActionClick}
                className="!px-3 !py-1.5 !text-sm"
              />
            </div>
          )}
        </div>

        {dismissible && onDismiss && (
          <IconButton
            icon={<X size={16} />}
            onClick={onDismiss}
            ariaLabel={resolvedDismissLabel}
            variant="ghost"
            size="sm"
            className={clsx('!border-transparent !shadow-none !translate-x-0 !translate-y-0', toneClasses.icon)}
            applyNeobrutalistEffects={false}
          />
        )}
      </div>
    </div>
  );
};

const ToastViewportItem: React.FC<{
  toast: StoredToast;
  placement: ToastPlacement;
  onDismiss: (id: string) => void;
}> = ({ toast, placement, onDismiss }) => {
  const timeoutRef = useRef<number | null>(null);

  const clearTimer = useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const scheduleDismiss = useCallback(() => {
    if (toast.duration <= 0) return;
    clearTimer();
    timeoutRef.current = window.setTimeout(() => {
      onDismiss(toast.id);
    }, toast.duration);
  }, [clearTimer, onDismiss, toast.duration, toast.id]);

  useEffect(() => {
    scheduleDismiss();
    return clearTimer;
  }, [clearTimer, scheduleDismiss]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: getAnimationOffset(placement), scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: getAnimationOffset(placement), scale: 0.96 }}
      transition={{ type: 'spring', stiffness: 340, damping: 28 }}
      className="pointer-events-auto"
      onMouseEnter={clearTimer}
      onMouseLeave={scheduleDismiss}
    >
      <Toast {...toast} onDismiss={() => onDismiss(toast.id)} />
    </motion.div>
  );
};

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  placement = 'bottom-right',
  maxVisible = 4,
  defaultDuration = 5000,
  portalContainer,
  translationNs,
}) => {
  const [toasts, setToasts] = useState<StoredToast[]>([]);
  const idRef = useRef(0);

  const dismissToast = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const dismissAll = useCallback(() => {
    setToasts([]);
  }, []);

  const showToast = useCallback((toast: ToastInput) => {
    const id = toast.id ?? `toast-${++idRef.current}`;
    const nextToast: StoredToast = {
      ...toast,
      id,
      duration: toast.duration ?? defaultDuration,
      translationNs: toast.translationNs ?? translationNs,
    };

    setToasts((current) => [...current, nextToast].slice(-maxVisible));
    return id;
  }, [defaultDuration, maxVisible, translationNs]);

  const createToneMethod = useCallback((tone: ToastTone) => {
    return (message: React.ReactNode, options?: Omit<ToastInput, 'message' | 'tone'>) =>
      showToast({ ...options, message, tone });
  }, [showToast]);

  const value = useMemo<ToastContextValue>(() => ({
    showToast,
    dismissToast,
    dismissAll,
    success: createToneMethod('success'),
    error: createToneMethod('error'),
    warning: createToneMethod('warning'),
    info: createToneMethod('info'),
  }), [createToneMethod, dismissAll, dismissToast, showToast]);

  useEffect(() => {
    globalToastApi = value;
    if (pendingToasts.length > 0) {
      const queuedToasts = pendingToasts;
      pendingToasts = [];
      queuedToasts.forEach((toastInput) => {
        value.showToast(toastInput);
      });
    }

    return () => {
      if (globalToastApi === value) {
        globalToastApi = null;
      }
    };
  }, [value]);

  const container = typeof document !== 'undefined' ? portalContainer ?? document.body : null;

  return (
    <ToastContext.Provider value={value}>
      {children}
      {container && ReactDOM.createPortal(
        <div className={clsx('pointer-events-none fixed z-[1200] flex w-full max-w-sm flex-col gap-3', placementClasses[placement])}>
          <AnimatePresence initial={false}>
            {toasts.map((toast) => (
              <ToastViewportItem
                key={toast.id}
                toast={toast}
                placement={placement}
                onDismiss={dismissToast}
              />
            ))}
          </AnimatePresence>
        </div>,
        container
      )}
    </ToastContext.Provider>
  );
};

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider.');
  }
  return context;
}

export type SnackbarProps = ToastProps;
export const Snackbar = Toast;

const enqueueToast = (toastInput: ToastInput) => {
  const id = toastInput.id ?? `pending-toast-${++pendingIdCounter}`;
  pendingToasts.push({ ...toastInput, id });
  return id;
};

export const toast: ToastApi = {
  show(toastInput: ToastInput) {
    return globalToastApi ? globalToastApi.showToast(toastInput) : enqueueToast(toastInput);
  },
  success(message: React.ReactNode, options?: Omit<ToastInput, 'message' | 'tone'>) {
    return toast.show({ ...options, message, tone: 'success' });
  },
  error(message: React.ReactNode, options?: Omit<ToastInput, 'message' | 'tone'>) {
    return toast.show({ ...options, message, tone: 'error' });
  },
  warning(message: React.ReactNode, options?: Omit<ToastInput, 'message' | 'tone'>) {
    return toast.show({ ...options, message, tone: 'warning' });
  },
  warn(message: React.ReactNode, options?: Omit<ToastInput, 'message' | 'tone'>) {
    return toast.show({ ...options, message, tone: 'warning' });
  },
  info(message: React.ReactNode, options?: Omit<ToastInput, 'message' | 'tone'>) {
    return toast.show({ ...options, message, tone: 'info' });
  },
  dismiss(id: string) {
    if (globalToastApi) {
      globalToastApi.dismissToast(id);
      return;
    }
    pendingToasts = pendingToasts.filter((toastInput) => toastInput.id !== id);
  },
  dismissAll() {
    if (globalToastApi) {
      globalToastApi.dismissAll();
      return;
    }
    pendingToasts = [];
  },
};
